import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Loader2, Sparkles, RefreshCw, Target, HelpCircle, ThumbsUp, AlertTriangle, ListChecks } from "lucide-react";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";

function Section({ icon: Icon, title, children, accent = "slate" }) {
  const accentMap = {
    amber: "bg-amber-50 text-amber-700",
    blue: "bg-blue-50 text-blue-700",
    emerald: "bg-emerald-50 text-emerald-700",
    rose: "bg-rose-50 text-rose-700",
    slate: "bg-slate-100 text-slate-700",
  };
  return (
    <div className="rounded-xl border border-black/5 p-4">
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-7 h-7 rounded-full flex items-center justify-center ${accentMap[accent] || accentMap.slate}`}>
          <Icon className="w-3.5 h-3.5" />
        </div>
        <h3 className="font-heading font-bold tracking-tight text-sm text-slate-900">{title}</h3>
      </div>
      {children}
    </div>
  );
}

export function InterviewPrepDialog({ interview, onClose }) {
  const [prep, setPrep] = useState(null);
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    if (!interview) { setPrep(null); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.post(`/candidate/applications/${interview.id}/interview-prep`);
        if (!cancelled) setPrep(data.prep);
      } catch (e) { toast.error(formatErr(e)); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [interview]);

  const regenerate = async () => {
    if (!interview) return;
    setRegenerating(true);
    try {
      const { data } = await api.post(`/candidate/applications/${interview.id}/interview-prep?force=true`);
      setPrep(data.prep);
      toast.success("Nova preparação gerada");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setRegenerating(false); }
  };

  return (
    <Dialog open={!!interview} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-violet-500" /> Preparação de entrevista IA
          </DialogTitle>
          {interview && (
            <p className="text-sm text-slate-500">{interview.job_title} · {interview.company_name}</p>
          )}
        </DialogHeader>

        {loading && !prep && (
          <div className="py-10 flex flex-col items-center gap-3">
            <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
            <p className="text-sm text-slate-500">A gerar o teu kit personalizado…</p>
          </div>
        )}

        {prep && (
          <div className="space-y-4 mt-2">
            {prep.focus_summary && (
              <div className="rounded-xl bg-gradient-to-br from-violet-50 to-fuchsia-50 border border-violet-100 p-4">
                <p className="text-sm text-slate-700 italic">{prep.focus_summary}</p>
              </div>
            )}

            {prep.likely_questions?.length > 0 && (
              <Section icon={HelpCircle} title={`Perguntas prováveis (${prep.likely_questions.length})`} accent="amber">
                <ul className="space-y-3">
                  {prep.likely_questions.map((q, i) => (
                    <li key={`q-${i}-${(q.question || "").slice(0, 40)}`} className="text-sm">
                      <p className="font-semibold text-slate-900">{i + 1}. {q.question}</p>
                      {q.answer_hint && <p className="text-slate-600 mt-1 text-xs pl-4">→ {q.answer_hint}</p>}
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {prep.strengths_to_highlight?.length > 0 && (
              <Section icon={ThumbsUp} title="Pontos a valorizar" accent="emerald">
                <ul className="space-y-1.5">
                  {prep.strengths_to_highlight.map((s, i) => (
                    <li key={`sth-${i}-${String(s).slice(0, 30)}`} className="text-sm text-slate-700 flex gap-2">
                      <span className="text-emerald-500">✓</span>{s}
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {prep.risks_to_prepare?.length > 0 && (
              <Section icon={AlertTriangle} title="Pontos a preparar" accent="rose">
                <ul className="space-y-1.5">
                  {prep.risks_to_prepare.map((r, i) => (
                    <li key={`risk-${i}-${String(r).slice(0, 30)}`} className="text-sm text-slate-700 flex gap-2">
                      <span className="text-rose-500">!</span>{r}
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {prep.candidate_questions?.length > 0 && (
              <Section icon={Target} title="Perguntas que deves fazer" accent="blue">
                <ul className="space-y-1.5">
                  {prep.candidate_questions.map((q, i) => (
                    <li key={`cq-${i}-${String(q).slice(0, 30)}`} className="text-sm text-slate-700 flex gap-2">
                      <span className="text-blue-500">?</span>{q}
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {prep.practical_tips?.length > 0 && (
              <Section icon={ListChecks} title="Dicas práticas" accent="slate">
                <ul className="space-y-1.5">
                  {prep.practical_tips.map((t, i) => (
                    <li key={`tip-${i}-${String(t).slice(0, 30)}`} className="text-sm text-slate-700 flex gap-2">
                      <span className="text-slate-400">•</span>{t}
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            <div className="flex justify-between items-center pt-2">
              <Button variant="ghost" size="sm" onClick={regenerate} disabled={regenerating}
                className="text-slate-500">
                <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${regenerating ? "animate-spin" : ""}`} />
                Gerar nova versão
              </Button>
              <Button size="sm" onClick={onClose} className="rounded-full">Fechar</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
